'use strict'

import {IMPURITY_BAND_STATES} from '../constants'

/**
 * Currency symbols + fee-code labels for upstream-refinery contracts.
 *
 * REFERENCE-ONLY contract data. Compute-backed fee inputs
 * (metal_rates[].fee, otherFees[].perTozUsd, assayFee[metal].feeUsd) stay
 * $-labelled — they are USD-by-storage AND USD-by-compute; relabelling €
 * while persisting USD numbers is a sign-error footgun.
 *
 * Two adjacent enums share three codes (`wire`, `sample_return`,
 * `copper_addition`); their labels MUST agree across surfaces. Centralizing
 * them here eliminates drift between the contract editor, the settlement
 * dialog/card, the contract-change diff, and the ad-hoc fees panel.
 */

const CURRENCY_SYMBOLS = {
  USD: '$',
  EUR: '€',
  GBP: '£',
  CAD: 'C$',
  JPY: '¥',
  AUD: 'A$',
  CHF: 'CHF',
}

export const currencySymbol = (currency) => CURRENCY_SYMBOLS[currency] || '$'

const SHARED_FEE_CODE_LABELS = Object.freeze({
  wire: 'Wire fee',
  sample_return: 'Sample return',
  copper_addition: 'Copper addition',
})

export const OTHER_FEE_LABELS = Object.freeze({
  ...SHARED_FEE_CODE_LABELS,
  treatment: 'Treatment fee',
})

export const AD_HOC_FEE_LABELS = Object.freeze({
  ...SHARED_FEE_CODE_LABELS,
  impurity_charge: 'Impurity charge',
  treatment_overage: 'Treatment overage',
  other: 'Other',
})

export const otherFeeCodeLabel = (code) => OTHER_FEE_LABELS[code] || String(code)
export const adHocFeeCodeLabel = (code) => AD_HOC_FEE_LABELS[code] || String(code)

// Display labels for reference-fee-schedule impurity band states. Single home
// so the contract editor's band-state picker and the settlement dialog's
// read-only schedule mirror render identical wording for the same state.
const IMPURITY_BAND_STATE_LABELS = Object.freeze({
  [IMPURITY_BAND_STATES.FREE]: 'Free',
  [IMPURITY_BAND_STATES.CHARGE]: 'Charge',
  [IMPURITY_BAND_STATES.REJECTED]: 'Rejected',
  [IMPURITY_BAND_STATES.TO_REVIEW]: 'To review',
})

export const impurityBandStateLabel = (state) =>
  IMPURITY_BAND_STATE_LABELS[state] || state || '—'

// Lot-weight unit chrome: passthrough + metric_tonne plural only (no toz→ozt bridge).
const LOT_WEIGHT_UNIT_LABELS = {
  kg: 'kg',
  metric_tonne: 'metric tonnes',
}

export const lotWeightUnitDisplayLabel = (unit) => (
  LOT_WEIGHT_UNIT_LABELS[unit] || unit
)

export const formatLotWeightRequirement = (requirement) => {
  if (!requirement || !Number.isFinite(requirement.value)) return '—'
  const unit = lotWeightUnitDisplayLabel(requirement.unit)
  const kind = requirement.kind || 'requirement'
  const basis = String(requirement.basis || 'lot').replace(/_/g, ' ')
  const enforcement = requirement.enforcement === 'hard' ? 'hard' : 'advisory'
  return `${kind}: ${requirement.value} ${unit} ${basis} (${enforcement})`
}

/**
 * Render an SLA modifier as a single human-readable bullet, e.g.
 *   "AU ≤ 99.5% AND lot ≤ 12 kg → +2 business days"
 *
 * Falls back to `mod.notes` when no recognized condition is present so
 * hand-edited modifiers don't render blank. Currently consumed by the
 * master-lot builder's accountability card; lives here (rather than in the
 * component) so the contract editor preview and contract-change diff can adopt
 * the same wording without duplicating the formatter.
 */
export const formatSlaModifierBullet = (mod) => {
  if (!mod || typeof mod !== 'object') return ''
  const when = mod.when || {}
  const days = mod.addBusinessDays
  const daysLabel = Number.isFinite(days)
    ? `${days > 0 ? '+' : ''}${days} business day${Math.abs(days) === 1 ? '' : 's'}`
    : ''
  const parts = []
  if (
    when.metal
    && when.purityPctMin !== undefined && when.purityPctMin !== null
    && when.purityPctMax !== undefined && when.purityPctMax !== null
  ) {
    parts.push(`${String(when.metal).toUpperCase()} ${when.purityPctMin}%–${when.purityPctMax}%`)
  } else if (when.metal && when.purityPctMax !== undefined && when.purityPctMax !== null) {
    parts.push(`${String(when.metal).toUpperCase()} ≤ ${when.purityPctMax}%`)
  } else if (when.metal && when.purityPctMin !== undefined && when.purityPctMin !== null) {
    parts.push(`${String(when.metal).toUpperCase()} ≥ ${when.purityPctMin}%`)
  }
  if (when.lotWeightKgMax !== undefined && when.lotWeightKgMax !== null) {
    parts.push(`lot ≤ ${when.lotWeightKgMax} kg`)
  }
  if (when.lotWeightTozMax !== undefined && when.lotWeightTozMax !== null) {
    parts.push(`lot ≤ ${when.lotWeightTozMax} ${lotWeightUnitDisplayLabel('ozt')}`)
  }
  if (when.hasImpurityCharges === true) {
    parts.push('lot has impurity charges')
  }
  if (parts.length === 0) return mod.notes || ''
  const condition = parts.join(' AND ')
  return daysLabel ? `${condition} → ${daysLabel}` : condition
}
